import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/components/layout/AppLayout";
import TodayDashboard from "@/pages/TodayDashboard";
import CaseloadList from "@/pages/CaseloadList";
// FP3-061 — Provider Workboard (scratch surface for referrals,
// screenings, evaluations-in-process, watchlist). Decoupled from
// Caseload / Wizard / Health / Schedule / Reports.
import ProviderWorkboard from "@/pages/ProviderWorkboard";
// FP3-071 — Help Center MVP (static accordion guide).
import HelpCenter from "@/pages/HelpCenter";
// FP3-078C — Contact / Support static page.
import Contact from "@/pages/Contact";
import StudentWorkspace from "@/pages/StudentWorkspace";
import GoalBuilder from "@/pages/GoalBuilder";
import LiveDataCollection from "@/pages/LiveDataCollection";
import Schedule from "@/pages/Schedule";
import Progress from "@/pages/Progress";
import Notifications from "@/pages/Notifications";
import ProviderReports from "@/pages/ProviderReports";
import ProviderMessages from "@/pages/ProviderMessages";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminSharedCalendar from "@/pages/admin/AdminSharedCalendar";
import AdminProviders from "@/pages/admin/AdminProviders";
import AdminProviderDetail from "@/pages/admin/AdminProviderDetail";
import AdminStudents from "@/pages/admin/AdminStudents";
import AdminStudentOverview from "@/pages/admin/AdminStudentOverview";
// FP3-046 — Frame Practice Terms of Use acceptance gate.
import TermsGate from "@/pages/terms/TermsGate";
// FP3-078 — Student-data authorization acknowledgment modal
// (one-time professional checkpoint shown before Add Student /
// Import. NOT a paid customer agreement.)
import StudentDataAckModal from "@/components/StudentDataAckModal";
// FP3-047 — Public Explore page (no auth required).
import Explore from "@/pages/Explore";
import AdminCoverage from "@/pages/admin/AdminCoverage";
import AdminReports from "@/pages/admin/AdminReports";
import AdminProgress from "@/pages/admin/AdminProgress";
import AdminNumbers from "@/pages/admin/AdminNumbers";
import AdminAccess from "@/pages/admin/AdminAccess";
import AdminImport from "@/pages/admin/AdminImport";
import AdminAssignments from "@/pages/admin/AdminAssignments";
import AdminSchools from "@/pages/admin/AdminSchools";
import AdminServiceFidelity from "@/pages/admin/AdminServiceFidelity";
// FP3-037 — Super Admin Platform Control Center.
import PlatformDashboard from "@/pages/platform/PlatformDashboard";
// FP3-074 — Super Admin Organization Console (per-org detail page).
import OrganizationConsole from "@/pages/platform/OrganizationConsole";
// FP3-131 — Super Admin Duplicate Student Diagnostic (READ-ONLY).
import DuplicateStudentDiagnostic from "@/pages/platform/DuplicateStudentDiagnostic";
// FP3-043 Phase B — Clean Pilot Org Wizard (super_admin only).
import PilotSetup from "@/pages/platform/PilotSetup";
import ProviderServiceFidelity from "@/pages/ProviderServiceFidelity";
// FP3-035C — Reference Library (Iowa-Nebraska norms).
import ReferenceLibrary from "@/pages/ReferenceLibrary";
// FP3-055 — Supervisor Lens (primary providers reviewing
// assistant-delivered work on students they supervise).
import SupervisorLens from "@/pages/SupervisorLens";
// FP3-060 — IEP / MET Due Dates hub.
import DueDatesHub from "@/pages/DueDatesHub";
// FP3-042 — Account Security (profile + password) surface.
import AccountSecurity from "@/pages/account/AccountSecurity";
// FP3-049 — Workspace info surface (reachable from sidebar gear menu).
import WorkspaceInfo from "@/pages/account/WorkspaceInfo";
import ImportStudentsPage from "@/pages/ImportStudentsPage";
import Login from "@/pages/auth/Login";
import RequestAccess from "@/pages/auth/RequestAccess";
// FP3-069 — Direct Solo Provider Pilot signup (no SuperAdmin gate).
import StartPilot from "@/pages/auth/StartPilot";
import AcceptInvite from "@/pages/auth/AcceptInvite";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
// FP3-033 — Print & Display routes (no AppLayout / sidebar).
import PrintStudentGoals from "@/pages/print/PrintStudentGoals";
import DisplayStudentGoals from "@/pages/print/DisplayStudentGoals";
import PrintStudentProgressSummary from "@/pages/print/PrintStudentProgressSummary";
import DisplayStudentProgressSummary from "@/pages/print/DisplayStudentProgressSummary";
import PrintStudentSessions from "@/pages/print/PrintStudentSessions";
import PrintProviderSchedule from "@/pages/print/PrintProviderSchedule";
import DisplayProviderSchedule from "@/pages/print/DisplayProviderSchedule";
// FP3-035A — Student Viewer for the Articulation Screener.
import StudentViewer from "@/pages/StudentViewer";
// FP3-035B — Articulation screener result print + display routes.
import PrintAssessmentRun from "@/pages/print/PrintAssessmentRun";
import DisplayAssessmentRun from "@/pages/print/DisplayAssessmentRun";
// FP3-130 — Concise caseload progress-summary print report.
import PrintCaseloadProgressReport from "@/pages/print/PrintCaseloadProgressReport";
import { AuthProvider, ProtectedRoute, useAuth } from "@/lib/auth";

/**
 * FP3-029B3 — When an admin family user hits the bare `/` route,
 * route them to /admin/dashboard. Provider users keep landing on
 * Today. The redirect respects `?as_provider=` so a deliberately-
 * entered read-only provider workspace still works.
 */
function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <TodayDashboard />;
  const adminFamily = ["admin", "org_admin", "super_admin"].includes(
    user.permission_role || ""
  );
  const params = new URLSearchParams(window.location.search || "");
  if (adminFamily && !params.get("as_provider")) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  return <TodayDashboard />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        {/* FP3-078 — Mount the student-data authorization checkpoint
            modal at the App root so any call site can open it via
            the auth-context promise gate. No-ops when the user
            isn't signed in or has already acknowledged. */}
        <StudentDataAckModal />
        <Routes>
          {/* FP3-028 — Public auth-adjacent routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/request-access" element={<RequestAccess />} />
          {/* FP3-069 — Direct Solo Provider Pilot signup. Public,
              no SuperAdmin approval gate, no billing fields. */}
          <Route path="/start-pilot" element={<StartPilot />} />
          {/* FP3-030 — Public invite + password-reset routes */}
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* FP3-046 — Terms of Use acceptance gate. Mounted OUTSIDE
              the <ProtectedRoute> wrapper so users who haven't yet
              accepted can reach it without triggering the terms
              redirect loop. The component itself enforces "must be
              logged in" via useAuth. */}
          <Route path="/terms" element={<TermsGate />} />
          {/* FP3-047 — Public Explore page. Mounted OUTSIDE the
              <ProtectedRoute> wrapper so unauthenticated visitors
              can reach it. Renders without any /api calls. Logged-in
              visitors see a "Go to dashboard" CTA instead of "Sign
              in"; the page itself never touches protected data. */}
          <Route path="/explore" element={<Explore />} />
          {/* FP3-078C — Public Contact / Support page. Static mailto
              card; no /api calls; safe to expose to anonymous
              visitors so the StartPilot footer link works. */}
          <Route path="/contact" element={<Contact />} />

          {/* Authenticated app shell */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<RootRedirect />} />
            <Route path="/caseload" element={<CaseloadList />} />
            <Route path="/workboard" element={<ProviderWorkboard />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/students/:studentId" element={<StudentWorkspace />} />
            <Route path="/goals/new" element={<GoalBuilder />} />
            <Route path="/goals/:goalId" element={<GoalBuilder />} />
            <Route path="/schedule" element={<Schedule />} />
            <Route path="/progress" element={<Progress />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/reports" element={<ProviderReports />} />
            {/* FP3-035B-3 — Provider Service Fidelity overview. Backend
                pre-scopes to the caller's direct-service caseload, so
                this surface is safe to expose to any authenticated
                provider. */}
            <Route path="/reports/service-fidelity" element={<ProviderServiceFidelity />} />
            {/* FP3-035C — Reference Library. Server-side role
                visibility gates the data; the route is open to any
                authenticated user but non-SLP users see an empty
                state. */}
            <Route path="/references" element={<ReferenceLibrary />} />
            {/* FP3-055 — Supervisor Lens. Primary providers see
                assistant-delivered evidence on students they
                supervise. The component itself gates non-providers. */}
            <Route path="/supervision" element={<SupervisorLens />} />
            {/* FP3-060 — IEP / MET Due Dates hub. Visible to every
                signed-in user; server scopes per role + super-admin
                context. */}
            <Route path="/due-dates" element={<DueDatesHub />} />
            <Route path="/messages" element={<ProviderMessages />} />
            <Route path="/session" element={<LiveDataCollection />} />
            {/* FP3-029A — Solo provider import surface. Provider-gated
                inside the component itself (non-solo providers see a
                forbidden message). */}
            <Route path="/import" element={<ImportStudentsPage mode="solo" />} />
            {/* FP3-042 — Account Security (profile + password). Any
                authenticated user can reach this surface. Role /
                permission / org / status are not editable here; only
                first name, last name, email, and password. */}
            <Route path="/account/security" element={<AccountSecurity />} />
            {/* FP3-049 — Workspace info (Provider/Admin org context). */}
            <Route path="/account/workspace" element={<WorkspaceInfo />} />
            <Route path="/account" element={<Navigate to="/account/security" replace />} />
          </Route>

          {/* Admin-only views — gated by ProtectedRoute(adminOnly) */}
          <Route
            element={
              <ProtectedRoute adminOnly>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/calendar" element={<AdminSharedCalendar />} />
            <Route path="/admin/providers" element={<AdminProviders />} />
            <Route path="/admin/providers/:providerId" element={<AdminProviderDetail />} />
            <Route path="/admin/students" element={<AdminStudents />} />
            {/* FP3-044 Phase A — Admin Student Overview: student-centered
                multi-provider landing for admin family. Provider users
                still land on /students/:id (unchanged). */}
            <Route path="/admin/students/:studentId" element={<AdminStudentOverview />} />
            <Route path="/admin/coverage" element={<AdminCoverage />} />
            {/* FP3-035B-3 — Org-wide Service Fidelity overview. Admin-gated
                via ProtectedRoute(adminOnly). Backend enforces the role
                check too. */}
            <Route path="/admin/service-fidelity" element={<AdminServiceFidelity />} />
            <Route path="/admin/progress" element={<AdminProgress />} />
            <Route path="/admin/reports" element={<AdminReports />} />
            <Route path="/admin/numbers" element={<AdminNumbers />} />
            <Route path="/admin/access" element={<AdminAccess />} />
            <Route path="/admin/import" element={<AdminImport />} />
            <Route path="/admin/assignments" element={<AdminAssignments />} />
            <Route path="/admin/schools" element={<AdminSchools />} />
            {/* FP3-037 — Super Admin Platform Control Center. The
                page itself self-gates to permission_role==='super_admin'
                and renders a 403 card otherwise. Mounting it under the
                adminOnly route group keeps it next to its sibling
                admin views; the backend endpoints additionally
                enforce super_admin (FastAPI Depends(require_super_admin)). */}
            <Route path="/platform" element={<Navigate to="/platform/dashboard" replace />} />
            <Route path="/platform/dashboard" element={<PlatformDashboard />} />
            {/* FP3-074 — Per-organization Super Admin Console.
                super_admin only; the page itself redirects non-super
                callers to "/", and the backend endpoint enforces
                require_super_admin. */}
            <Route path="/platform/org/:orgId" element={<OrganizationConsole />} />
            <Route
              path="/platform/duplicate-student-diagnostic"
              element={<DuplicateStudentDiagnostic />}
            />
            {/* FP3-043 Phase B — Clean Pilot Org Wizard. super_admin
                only; the page itself defensively redirects non-super
                callers to "/" and the backend endpoint enforces
                require_super_admin. */}
            <Route path="/platform/pilot-setup" element={<PilotSetup />} />
          </Route>

          {/* FP3-033 — Print & Display routes. Authenticated (via
              ProtectedRoute) but DELIBERATELY rendered OUTSIDE the
              AppLayout so the sidebar/nav/edit chrome don't bleed
              into the print/display output. Each underlying route
              still calls existing authenticated APIs which enforce
              the original access rules. */}
          <Route
            path="/print/student/:studentId/goals"
            element={<ProtectedRoute><PrintStudentGoals /></ProtectedRoute>}
          />
          <Route
            path="/display/student/:studentId/goals"
            element={<ProtectedRoute><DisplayStudentGoals /></ProtectedRoute>}
          />
          <Route
            path="/print/student/:studentId/progress-summary"
            element={<ProtectedRoute><PrintStudentProgressSummary /></ProtectedRoute>}
          />
          <Route
            path="/display/student/:studentId/progress-summary"
            element={<ProtectedRoute><DisplayStudentProgressSummary /></ProtectedRoute>}
          />
          <Route
            path="/print/student/:studentId/sessions"
            element={<ProtectedRoute><PrintStudentSessions /></ProtectedRoute>}
          />
          <Route
            path="/print/provider/:providerId/schedule"
            element={<ProtectedRoute><PrintProviderSchedule /></ProtectedRoute>}
          />
          <Route
            path="/display/provider/:providerId/schedule"
            element={<ProtectedRoute><DisplayProviderSchedule /></ProtectedRoute>}
          />

          {/* FP3-035B — Articulation screener result print + display.
              Same auth gate as other print routes; the underlying
              GET endpoints enforce visibility. */}
          <Route
            path="/print/students/:studentId/assessment-runs/:runId"
            element={<ProtectedRoute><PrintAssessmentRun /></ProtectedRoute>}
          />
          <Route
            path="/display/students/:studentId/assessment-runs/:runId"
            element={<ProtectedRoute><DisplayAssessmentRun /></ProtectedRoute>}
          />

          {/* FP3-130 — Concise printable caseload progress report.
              Query params (all optional):
                ?student_ids=<csv>   omit = full caseload
                &start=<YYYY-MM-DD>  omit = active period start
                &end=<YYYY-MM-DD>    omit = today
              Uses the same PrintShell as other print routes. */}
          <Route
            path="/print/progress-summary"
            element={<ProtectedRoute><PrintCaseloadProgressReport /></ProtectedRoute>}
          />

          {/* FP3-035A — Student Viewer for the Articulation Screener.
              PUBLIC by secure session token: shows ONLY the picture
              prompt + target word + sound + position. No PHI, no
              scoring, no provider identity. Token is minted server-
              side and revoked when the provider ends the session. */}
          <Route path="/student-viewer/:token" element={<StudentViewer />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
